import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ExportFileConfig, IFilePickerService, ImportFileConfig } from "@rvi/asset-modules";
import { FileManagerComponent, FileManagerConfig } from "./file-manager.component";
import { FileManagerService } from "./file-manager.service";
import { FileInfo, OperationMode, SelectionMode, WriteFileRequest } from "./types";
import { IVisolFilePickerService, VisolExportConfig } from "./visol-file-picker.service";

@Injectable()
export class ElectronFilePickerService implements IFilePickerService, IVisolFilePickerService {
  constructor(
    private readonly dialog: MatDialog,
    private fileManagerService: FileManagerService
  ) {}
  importFiles({ multiple, extensions, folder }: ImportFileConfig & { folder?: boolean }): Promise<File[]> {
    return new Promise((resolve) => {
      let selectionMode: SelectionMode;
      if (folder) {
        selectionMode = multiple ? SelectionMode.MultipleFolders : SelectionMode.SingleFolder;
      } else {
        selectionMode = multiple ? SelectionMode.MultipleFiles : SelectionMode.SingleFile;
      }
      const config: MatDialogConfig<FileManagerConfig> = {
        disableClose: true,
        ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
        data: {
          operationMode: OperationMode.Import,
          selectionMode,
          extensions: extensions ?? [],
          handleImportedSelection: (files: File[]) => resolve(files)
        }
      };
      this.dialog.open(FileManagerComponent, config);
    });
  }
  async exportFile(config: ExportFileConfig): Promise<void> {
    await this.exportFileWithResult(config);
  }

  exportFileWithResult(config: VisolExportConfig): Promise<boolean> {
    return new Promise((resolve) => {
      let saved = false;

      const handleExportedSelection = async (selectedFiles: FileInfo[]): Promise<void> => {
        if (selectedFiles.length === 1) {
          const { folderPath, fileName } = selectedFiles[0];

          const buffer = await config.content.arrayBuffer();
          const data: Buffer = Buffer.from(buffer);

          const writeFileRequest: WriteFileRequest = {
            folderPath,
            fileName,
            mimeType: "",
            data
          };

          await this.fileManagerService.writeFiles([writeFileRequest]);
          saved = true;
        }
      };

      const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
        disableClose: true,
        ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
        data: {
          operationMode: OperationMode.Export,
          selectionMode: SelectionMode.SingleFileOrFolder,
          // The file manager needs the extension of the file in order to allow it to be exported
          extensions: [this.getFileExtension(config.filename)],
          fileName: config.filename,
          restrictSystemDriveToRoot: config.restrictSystemDriveToRoot ?? false,
          handleExportedSelection
        }
      };

      const dialogRef = this.dialog.open(FileManagerComponent, fileManagerConfig);

      // Resolves on cancel too — the exported-selection callback only runs when the user confirms a save.
      dialogRef.afterClosed().subscribe(() => resolve(saved));
    });
  }

  private getFileExtension(filename: string): string | undefined {
    const parts = filename.split(".");
    if (parts.length > 1) {
      return parts.pop();
    }
    return undefined;
  }
}
